import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import { MM } from "@pdfking/quantity"
import { HorizontalAlign } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"
import type { CalendarArea, CalendarDesign } from "#internal"

import { Slice } from "../../layout/Slice"
import { Area } from "../Area"

// As the shop's templates set them, the same on every size: the logo's
// height and the number's
const LOGO = MM(9)
const FONT = MM(4)
// The width the binding's perforation is drawn down the middle of, on the
// security's edge
const STAPLES = MM(2)

/** A numbered pad of stubs and counterfoils, every size alike: its sheets
 * torn along a perforation, the stub on the left within its security — the
 * staple's room off its left edge —, the counterfoil on the right, the two
 * laid out the same: the logo on the left of its foot, the sheet's number on
 * its right, both standing on it; a perforation by the staples too. Its base is the book's cover round the pad,
 * as the other pads' (see GenerateContent): none of its own. */
export const Counterfoil: CalendarDesign = {
  cover: () => [],
  page: ({ gap, labels, outer, page }, number) => {
    const [stub, foil] = Slice.grid(outer, 2, 1, Scale(gap, 2))
    const label = `N. ${`${number}`.padStart(4, "0")}`
    // A part: the logo and the number along its foot
    const part = (box: SpatialRectangle<Distance>): CalendarArea[] => {
      const [row] = Slice.bottom(box, LOGO, gap)
      const [logo, rest] = Slice.left(row, LOGO, gap)
      return [
        Area.logo(logo, labels),
        Area.text({ ...rest, height: FONT }, label, PALETTE.secondary, HorizontalAlign.RIGHT),
      ]
    }
    const dashes = Scale(gap, 2)
    // The binding's line too, on the security's edge on the side it is
    // stapled, the wider margin: the left on a front, the right on a rear
    const right = Subtract(Total(page.x, page.width), Total(outer.x, outer.width))
    const binding = right > Subtract(outer.x, page.x) ? Total(outer.x, outer.width) : outer.x
    return [
      Area.perforation({ ...page, width: STAPLES, x: Subtract(binding, Scale(STAPLES, 0.5)) }),
      ...part(stub!),
      Area.perforation({ ...Slice.after(page, Total(stub!.x, stub!.width)), width: dashes }),
      ...part(foil!),
    ]
  },
  pages: "pad",
}
